import React, { useEffect, useMemo, useState } from "react";
import {
  EnvironmentOutlined,
  GithubOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { theme } from "antd";
import { useResumeStore } from "../../../store/resumeStore.jsx";
import {
  formatDateRange,
  getAchievements,
  getActiveEducation,
  getActiveExperiences,
  getAboutText,
  getExperienceHighlights,
  getInitials,
  getLanguages,
  getObjectiveText,
  getPrimaryContactItems,
  getProjects,
  getSkills,
  getSocialItems,
  getSummaryText,
  normalizeExternalUrl,
  ResumeRichTextInline,
  stripHtml,
} from "../resumeTemplateUtils.js";

const { useToken } = theme;
const DEFAULT_THEME_PRIMARY = "#1677ff";

const PROTOTYPE_CONFIGS = {
  template20: {
    name: "Orbit Executive",
    source: "01-orbit-executive.svg",
    layout: "orbit",
    accent: "#a76b2c",
    dark: "#20354f",
    page: "#f6f1e9",
    paper: "#fffdf8",
    text: "#192635",
    muted: "#607085",
    border: "#ded2bd",
    font: "Arial, Helvetica, sans-serif",
  },
  template21: {
    name: "SaaS Bento",
    source: "02-saas-bento.svg",
    layout: "bento",
    accent: "#2563eb",
    dark: "#0f172a",
    page: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 54%, #fff7ed 100%)",
    paper: "rgba(255,255,255,0.86)",
    text: "#0f172a",
    muted: "#475569",
    border: "#cde8ff",
    font: "Arial, Helvetica, sans-serif",
  },
  template22: {
    name: "Mono Ledger",
    source: "03-mono-ledger.svg",
    layout: "ledger",
    accent: "#111111",
    dark: "#111111",
    page: "#fbfaf7",
    paper: "#ffffff",
    text: "#111111",
    muted: "#555555",
    border: "#111111",
    font: "Arial, Helvetica, sans-serif",
  },
  template23: {
    name: "Coral Rail",
    source: "04-coral-rail.svg",
    layout: "rail",
    accent: "#e36f5b",
    dark: "#b94e43",
    page: "#fff7f2",
    paper: "#fff7f2",
    text: "#2b2f38",
    muted: "#6b7280",
    border: "#fad3c8",
    font: "Arial, Helvetica, sans-serif",
  },
  template24: {
    name: "Neon Command",
    source: "05-neon-command.svg",
    layout: "neon",
    accent: "#00d4c8",
    dark: "#050607",
    page: "#050607",
    paper: "#050607",
    text: "#f8fafc",
    muted: "#cbd5e1",
    border: "#00d4c8",
    font: "Arial, Helvetica, sans-serif",
  },
  template25: {
    name: "Emerald Timeline",
    source: "06-emerald-timeline.svg",
    layout: "timeline",
    accent: "#16a34a",
    dark: "#064e3b",
    page: "#f0fdf4",
    paper: "#ffffff",
    text: "#0f172a",
    muted: "#64748b",
    border: "#bbf7d0",
    font: "Arial, Helvetica, sans-serif",
  },
  template26: {
    name: "Editorial Serene",
    source: "07-editorial-serene.svg",
    layout: "editorial",
    accent: "#9a6738",
    dark: "#1d1a16",
    page: "#f4efe6",
    paper: "#f4efe6",
    text: "#1d1a16",
    muted: "#5f5a52",
    border: "#dcc9af",
    font: "Georgia, 'Times New Roman', serif",
  },
  template27: {
    name: "Product Card",
    source: "08-product-card.svg",
    layout: "product",
    accent: "#4f46e5",
    dark: "#0e1628",
    page: "#0e1628",
    paper: "#f8fafc",
    text: "#0f172a",
    muted: "#475569",
    border: "#dbe3ee",
    font: "Arial, Helvetica, sans-serif",
  },
  template28: {
    name: "Slate Grid",
    source: "09-slate-grid.svg",
    layout: "slate",
    accent: "#2563eb",
    dark: "#1e293b",
    page: "#e2e8f0",
    paper: "#ffffff",
    text: "#0f172a",
    muted: "#64748b",
    border: "#cbd5e1",
    font: "Arial, Helvetica, sans-serif",
  },
  template29: {
    name: "Founder Board",
    source: "10-founder-board.svg",
    layout: "founder",
    accent: "#f97316",
    dark: "#111827",
    page: "#fff7ed",
    paper: "#ffffff",
    text: "#111827",
    muted: "#4b5563",
    border: "#e5e7eb",
    font: "Arial, Helvetica, sans-serif",
  },
  template30: {
    name: "Ivory Serif",
    source: "11-ivory-serif.svg",
    layout: "ivory",
    accent: "#b88a44",
    dark: "#2d2418",
    page: "#f8f5ef",
    paper: "#ffffff",
    text: "#2d2418",
    muted: "#6b6258",
    border: "#d8c9ad",
    font: "Georgia, 'Times New Roman', serif",
  },
  template31: {
    name: "Graphite Split",
    source: "12-graphite-split.svg",
    layout: "graphite",
    accent: "#ef4444",
    dark: "#2f343f",
    page: "#111827",
    paper: "#f9fafb",
    text: "#111827",
    muted: "#374151",
    border: "#d1d5db",
    font: "Arial, Helvetica, sans-serif",
  },
  template32: {
    name: "Sky Clean",
    source: "13-sky-clean.svg",
    layout: "sky",
    accent: "#0284c7",
    dark: "#0f172a",
    page: "#ffffff",
    paper: "#ffffff",
    text: "#0f172a",
    muted: "#475569",
    border: "#bae6fd",
    font: "Arial, Helvetica, sans-serif",
  },
  template33: {
    name: "Luxury Rail",
    source: "14-luxury-rail.svg",
    layout: "luxury",
    accent: "#c49a4a",
    dark: "#11100d",
    page: "#11100d",
    paper: "#fbf6ea",
    text: "#11100d",
    muted: "#5c5347",
    border: "#d5bd87",
    font: "Georgia, 'Times New Roman', serif",
  },
  template34: {
    name: "Compact ATS",
    source: "15-compact-ats.svg",
    layout: "ats",
    accent: "#2563eb",
    dark: "#1f2937",
    page: "#ffffff",
    paper: "#ffffff",
    text: "#1f2937",
    muted: "#4b5563",
    border: "#d1d5db",
    font: "Arial, Helvetica, sans-serif",
  },
};

const GENERATED_TEMPLATE_IDS = Object.keys(PROTOTYPE_CONFIGS);

export const PROTOTYPE_FIDELITY_SPECS = {
  template20: {
    composition: "inset-rounded-paper-orbit-avatar",
    sectionOrder: ["header", "about-dark-band", "skills", "experience-cards", "education-achievements"],
    visualSystem: "ivory-navy-copper-rounded",
  },
  template21: {
    composition: "gradient-bento-dashboard",
    sectionOrder: ["header", "about-metric", "objective-skills", "experience-panel", "education-achievements"],
    visualSystem: "soft-gradient-blue-bento",
  },
  template22: {
    composition: "monochrome-ledger-frame",
    sectionOrder: ["ledger-label", "header", "about", "numbered-experience", "skills", "education-achievements"],
    visualSystem: "black-white-ledger-lines",
  },
  template23: {
    composition: "left-coral-rail",
    sectionOrder: ["sidebar-profile-contact-skills", "main-header", "about", "experience", "education-achievements"],
    visualSystem: "coral-sidebar-rounded-cards",
  },
  template24: {
    composition: "dark-command-console",
    sectionOrder: ["avatar-sidebar", "terminal-header", "expertise-contact", "about", "work", "education-achievements"],
    visualSystem: "black-neon-cyan-command",
  },
  template25: {
    composition: "emerald-header-timeline",
    sectionOrder: ["dark-header", "about", "skills-square", "vertical-timeline", "education-achievements"],
    visualSystem: "emerald-clean-timeline",
  },
  template26: {
    composition: "editorial-serif-story",
    sectionOrder: ["split-editorial-header", "about", "selected-work", "skills", "education-achievements"],
    visualSystem: "serif-warm-editorial",
  },
  template27: {
    composition: "dark-product-shell-card-stack",
    sectionOrder: ["colored-header-card", "about-objective", "experience", "skills", "education-achievements"],
    visualSystem: "indigo-product-cards",
  },
  template28: {
    composition: "slate-top-band-grid",
    sectionOrder: ["dark-header-band", "about-skills-grid", "experience-grid", "education-achievements"],
    visualSystem: "slate-blue-square-grid",
  },
  template29: {
    composition: "founder-board-pitch",
    sectionOrder: ["dark-founder-header", "value-proposition", "skills-board", "experience", "education-achievements"],
    visualSystem: "orange-founder-board",
  },
  template30: {
    composition: "ivory-serif-formal",
    sectionOrder: ["formal-header", "about", "experience-ledger", "skills-band", "education-achievements"],
    visualSystem: "ivory-serif-gold-rule",
  },
  template31: {
    composition: "graphite-left-split",
    sectionOrder: ["sidebar-profile-contact-skills", "about", "experience-cards", "education", "achievements"],
    visualSystem: "graphite-red-split",
  },
  template32: {
    composition: "sky-clean-top-banner",
    sectionOrder: ["blue-avatar-header", "about", "skills-pills", "timeline", "education-achievements"],
    visualSystem: "white-sky-clean",
  },
  template33: {
    composition: "luxury-top-rail",
    sectionOrder: ["gold-rail-header", "dark-about-band", "skills", "experience-cards", "education-achievements"],
    visualSystem: "black-cream-gold-luxury",
  },
  template34: {
    composition: "compact-ats-single-column",
    sectionOrder: ["header-rule", "about", "objective", "skills-inline", "experience-ledger", "education", "achievements"],
    visualSystem: "white-blue-ats",
  },
};

function getPrototypeSignature(templateId) {
  const spec = PROTOTYPE_FIDELITY_SPECS[templateId];
  if (!spec) return "";
  return [spec.composition, spec.sectionOrder.join(">"), spec.visualSystem].join("|");
}

function normalizeColor(value = "") {
  return String(value || "").trim().toLowerCase();
}

function pickAccent(token, fallback) {
  const primary = normalizeColor(token?.colorPrimary);
  return primary && primary !== DEFAULT_THEME_PRIMARY ? token.colorPrimary : fallback;
}

function withAlpha(color, alpha = "18") {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : color;
}

function clean(value) {
  if (value == null || value === false) return "";
  if (React.isValidElement(value)) return "";
  if (Array.isArray(value)) return value.map(clean).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return clean(value.value || value.label || value.name || value.title || value.description || "");
  }

  return stripHtml(String(value))
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function asList(value) {
  if (React.isValidElement(value)) return [];
  if (Array.isArray(value)) return value.flatMap(asList);
  if (value && typeof value === "object" && !React.isValidElement(value)) {
    const preferred = clean(value.title || value.name || value.label || value.value || value.description || "");
    return preferred ? [preferred] : Object.values(value).flatMap(asList);
  }
  return String(clean(value) || "")
    .split(/\n|;|\|/g)
    .map((item) => clean(item))
    .filter(Boolean);
}

function getByPath(source, path) {
  return path.split(".").reduce((value, key) => value?.[key], source);
}

function optionalList(data, keys) {
  const seen = new Set();
  return keys
    .flatMap((key) => asList(getByPath(data, key)))
    .filter((item) => {
      const normalized = String(item || "").toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function getCustomSectionItems(data) {
  if (!Array.isArray(data?.customSections)) return [];

  return data.customSections.flatMap((section) => {
    if (!section || typeof section !== "object") return asList(section);
    const title = clean(section.title || section.name || section.label);
    const items = asList(
      section.items ||
        section.entries ||
        section.content ||
        section.description ||
        section.value,
    );
    if (!items.length) return title ? [title] : [];
    return items.map((item) => (title ? `${title}: ${item}` : item));
  });
}

function readExperienceLevel() {
  if (typeof window === "undefined") return "experienced";
  const value = String(window.sessionStorage?.getItem("experienceLevel") || "").trim().toLowerCase();
  if (value === "fresher") return "fresher";
  if (value === "experience" || value === "experienced") return "experienced";
  return "experienced";
}

function useExperienceLevel() {
  const [level, setLevel] = useState(readExperienceLevel);

  useEffect(() => {
    const updateLevel = () => setLevel(readExperienceLevel());
    window.addEventListener("storage", updateLevel);
    window.addEventListener("resumeiq:experience-level-change", updateLevel);
    return () => {
      window.removeEventListener("storage", updateLevel);
      window.removeEventListener("resumeiq:experience-level-change", updateLevel);
    };
  }, []);

  return level;
}

function linkFor(value) {
  const text = clean(value);
  if (!text || React.isValidElement(text)) return "";
  if (/@/.test(text) && !/^https?:/i.test(text)) return `mailto:${text}`;
  if (/^\+?\d[\d\s()+-]{5,}$/.test(text)) return `tel:${text.replace(/\s+/g, "")}`;
  if (/\.[a-z]{2,}/i.test(text) || /^(linkedin|github)\.com/i.test(text)) return normalizeExternalUrl(text);
  return "";
}

function socialLabel(key = "", value = "") {
  const normalizedKey = String(key || "").toLowerCase();
  if (normalizedKey === "linkedin") return "LinkedIn";
  if (normalizedKey === "github") return "GitHub";
  const label = clean(value).replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  if (/linkedin\.com/i.test(label)) return "LinkedIn";
  if (/github\.com/i.test(label)) return "GitHub";
  return label.split("/")[0] || "Link";
}

function usePrototypeModel() {
  const data = useResumeStore((state) => state.resume);
  const experiences = getActiveExperiences(data);
  const projects = getProjects(data, 8);
  const certifications = optionalList(data, ["certifications", "certification", "certificates", "licenses"]);
  const training = optionalList(data, ["training", "trainings", "workshops", "courses"]);
  const internships = optionalList(data, ["internships", "internship", "academicInternships"]);
  const leadership = optionalList(data, ["leadership", "leadershipExperience", "managementExperience"]);
  const domains = optionalList(data, ["domainExpertise", "domains", "industryKnowledge"]);
  const awards = optionalList(data, ["awards", "honors", "recognition"]);
  const publications = optionalList(data, ["publications", "articles"]);
  const customSections = getCustomSectionItems(data);

  return {
    data,
    name: clean(data?.name) || "Your Name",
    role: clean(data?.roleTitle || data?.role) || "Professional Title",
    initials: getInitials(data?.name || "CV") || "CV",
    about: getAboutText(data) || getSummaryText(data),
    objective: getObjectiveText(data),
    totalExperience: clean(data?.totalExperience || data?.relevantExperience),
    currentCompany: clean(experiences[0]?.company),
    previousCompanies: experiences.slice(1).map((item) => clean(item.company)).filter(Boolean),
    experiences,
    education: getActiveEducation(data),
    skills: getSkills(data),
    languages: getLanguages(data),
    contacts: getPrimaryContactItems(data),
    socials: getSocialItems(data),
    projects,
    certifications,
    training,
    internships,
    leadership,
    domains,
    awards,
    publications,
    customSections,
    achievements: getAchievements(data),
  };
}

function hasChildren(value) {
  return React.Children.toArray(value).some(Boolean);
}

function PrototypeStyles({ config }) {
  return (
    <style>{`
      .np-root, .np-root * { box-sizing: border-box; }
      .np-root {
        width: 100%;
        min-height: 100%;
        color: ${config.text};
        font-family: ${config.font};
        line-height: 1.42;
        word-break: normal;
        overflow-wrap: break-word;
      }
      .np-section,
      .np-card,
      .np-item {
        break-inside: auto;
        page-break-inside: auto;
      }
      .np-section-title {
        margin: 0 0 10px;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .np-grid-two { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
      .np-main-grid { display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: 24px; }
      .np-chip-wrap { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
      .np-chip {
        display: inline-flex;
        max-width: 100%;
        border-radius: 999px;
        padding: 5px 10px;
        font-size: 11.5px;
        font-weight: 800;
        line-height: 1.12;
      }
      .np-list { margin: 0; padding-left: 16px; }
      .np-list li { margin-bottom: 3px; }
      .np-contact-row { display: flex; flex-wrap: wrap; gap: 6px 14px; align-items: center; }
      .np-link { color: inherit; text-decoration: none; }
      .np-link-icon {
        display: inline-flex;
        width: 24px;
        height: 24px;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        text-decoration: none;
      }
      @media print {
        .np-root,
        .np-root * {
          box-shadow: none !important;
          text-shadow: none !important;
          filter: none !important;
          backdrop-filter: none !important;
        }
      }
      @media (max-width: 820px) {
        .np-main-grid,
        .np-grid-two { grid-template-columns: 1fr !important; }
        .np-root { min-width: 0 !important; }
      }
    `}</style>
  );
}

function Shell({ config, children, style = {} }) {
  return (
    <div
      className="np-root"
      data-template-family="new-prototype"
      data-source-prototype={config.source}
      data-prototype-layout={config.layout}
      data-prototype-signature={getPrototypeSignature(config.templateId)}
      style={{ background: config.page, padding: 20, ...style }}
    >
      <PrototypeStyles config={config} />
      {children}
    </div>
  );
}

function Paper({ config, children, style = {} }) {
  return (
    <main
      data-resume-root="true"
      style={{
        minHeight: "100%",
        background: config.paper,
        border: `1px solid ${config.border}`,
        color: config.text,
        padding: 36,
        ...style,
      }}
    >
      {children}
    </main>
  );
}

function Section({ title, config, children, style = {}, titleStyle = {}, dark = false, sectionKey }) {
  if (!hasChildren(children)) return null;
  const accent = config.dynamicAccent || config.accent;
  return (
    <section className="np-section cv-section" data-section={sectionKey || (title ? title.toLowerCase().replace(/\s+/g, "-") : undefined)} style={style}>
      {title ? (
        <h2 className="np-section-title" style={{ color: dark ? accent : accent, ...titleStyle }}>
          {title}
        </h2>
      ) : null}
      {children}
    </section>
  );
}

function Header({ model, config, layout = "default", dark = false, style = {} }) {
  const color = dark ? "#ffffff" : config.text;
  const muted = dark ? withAlpha("#ffffff", "cc") : config.muted;

  return (
    <header className="cv-section" data-section="header" data-cv-kind="header" style={style}>
      {layout === "avatar-left" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "74px minmax(0, 1fr)", gap: 18, alignItems: "center" }}>
            <Avatar model={model} config={config} size={70} dark={dark} />
            <HeaderText model={model} config={config} color={color} muted={muted} />
          </div>
          <ContactStrip model={model} config={config} dark={dark} style={{ marginTop: 10 }} />
        </>
      ) : layout === "split" ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 18, alignItems: "start" }}>
          <div>
            <HeaderText model={model} config={config} color={color} muted={muted} />
            <ContactStrip model={model} config={config} dark={dark} style={{ marginTop: 10 }} />
          </div>
          <Avatar model={model} config={config} size={96} dark={dark} />
        </div>
      ) : (
        <>
          <HeaderText model={model} config={config} color={color} muted={muted} />
          <ContactStrip model={model} config={config} dark={dark} style={{ marginTop: 10 }} />
        </>
      )}
    </header>
  );
}

function HeaderText({ model, config, color, muted }) {
  const isSerif = /Georgia|Times/i.test(config.font);
  return (
    <>
      <h1 data-field="name" style={{ margin: 0, color, fontSize: isSerif ? 42 : 38, lineHeight: 1.02, fontWeight: 900 }}>
        {model.name}
      </h1>
      <div data-field="roleTitle" style={{ marginTop: 7, color: muted, fontSize: 13.5, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "Arial, Helvetica, sans-serif" }}>
        {model.role}
      </div>
    </>
  );
}

function Avatar({ model, config, size = 84, dark = false, style = {} }) {
  const accent = config.dynamicAccent || config.accent;
  const photo = clean(model.data?.photo);
  const baseStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    background: dark ? "#15191f" : "#ffffff",
    border: `2px solid ${accent}`,
    color: accent,
    fontSize: Math.round(size * 0.36),
    fontWeight: 900,
    ...style,
  };

  if (photo) {
    return <img src={photo} alt="" style={{ ...baseStyle, objectFit: "cover" }} />;
  }

  return <div aria-hidden="true" style={baseStyle}>{model.initials}</div>;
}

function ContactStrip({ model, config, dark = false, style = {}, iconsOnly = false }) {
  const accent = config.dynamicAccent || config.accent;
  const color = dark ? "#e5e7eb" : config.muted;
  const icons = {
    location: <EnvironmentOutlined />,
    phone: <PhoneOutlined />,
    email: <MailOutlined />,
  };

  return (
    <div className="np-contact-row" data-section="contact" style={{ color, fontSize: 11.8, ...style }}>
      {model.contacts.map((item) => {
        const href = linkFor(item.value);
        const content = (
          <>
            <span style={{ color: accent, marginRight: 5 }}>{icons[item.key] || <GlobalOutlined />}</span>
            {iconsOnly ? null : <span>{item.value}</span>}
          </>
        );
        return href ? (
          <a
            key={`${item.key}-${item.value}`}
            className="np-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color }}
          >
            {content}
          </a>
        ) : (
          <span key={`${item.key}-${item.value}`}>{content}</span>
        );
      })}
      <SocialLinks items={model.socials} config={config} dark={dark} />
    </div>
  );
}

function SocialLinks({ items, config, dark = false }) {
  if (!items.length) return null;
  const accent = config.dynamicAccent || config.accent;
  const color = dark ? "#ffffff" : config.text;
  const icons = { linkedin: <LinkedinOutlined />, github: <GithubOutlined /> };

  return (
    <>
      {items.map((item) => {
        const href = linkFor(item.value);
        if (!href) return null;
        return (
          <a
            key={`${item.key}-${item.value}`}
            className="np-link-icon"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={socialLabel(item.key, item.value)}
            title={socialLabel(item.key, item.value)}
            style={{ color, border: `1px solid ${withAlpha(accent, "66")}`, background: dark ? withAlpha(accent, "20") : withAlpha(accent, "12") }}
          >
            {icons[item.key] || <GlobalOutlined />}
          </a>
        );
      })}
    </>
  );
}

function TextBlock({ text, color, style = {}, dataField = "summary" }) {
  if (!clean(text)) return null;
  return <p data-field={dataField} style={{ margin: 0, color, fontSize: 13, lineHeight: 1.44, ...style }}>{text}</p>;
}

function Skills({ items, config, mode = "chips", dark = false, limit = Number.MAX_SAFE_INTEGER }) {
  const skills = items.slice(0, limit);
  if (!skills.length) return null;
  const accent = config.dynamicAccent || config.accent;
  const textColor = dark ? "#ffffff" : config.text;

  if (mode === "bars") {
    return (
      <div style={{ display: "grid", gap: 7 }}>
        {skills.map((skill, index) => (
          <div key={`${skill}-${index}`} data-section="skillItem" data-field="skill">
            <div style={{ color: textColor, fontSize: 11.5, marginBottom: 3 }}>{skill}</div>
            <div style={{ height: 4, background: dark ? "#334155" : withAlpha(accent, "24") }}>
              <div style={{ width: `${84 - (index % 4) * 8}%`, height: "100%", background: accent }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (mode === "inline") {
    return <p data-section="skills" style={{ margin: 0, color: textColor, fontSize: 12.8, lineHeight: 1.45 }}>{skills.join("  |  ")}</p>;
  }

  return (
    <div className="np-chip-wrap">
      {skills.map((skill, index) => (
        <span
          key={`${skill}-${index}`}
          className="np-chip"
          data-section="skillItem"
          data-field="skill"
          style={{
            border: `1px solid ${withAlpha(accent, "66")}`,
            background: dark ? withAlpha(accent, "1c") : withAlpha(accent, "12"),
            color: dark ? "#ffffff" : config.text,
            borderRadius: mode === "square" ? 7 : 999,
          }}
        >
          {skill}
        </span>
      ))}
    </div>
  );
}

function ExperienceList({ items, config, mode = "card", dark = false, numbered = false }) {
  if (!items.length) return null;
  const accent = config.dynamicAccent || config.accent;
  const textColor = dark ? "#ffffff" : config.text;
  const muted = dark ? "#cbd5e1" : config.muted;

  return (
    <div style={{ display: "grid", gap: mode === "ledger" ? 10 : 8 }}>
      {items.map((item, index) => {
        const highlights = getExperienceHighlights(item, 4);
        const date = formatDateRange(item.startDate, item.endDate, item.years);
        const body = (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 8, alignItems: "baseline" }}>
              <h3 data-field="jobRole" style={{ margin: 0, color: textColor, fontSize: mode === "ledger" ? 18 : 15.5, lineHeight: 1.18, fontWeight: 900 }}>{item.role || "Role"}</h3>
              {date ? <span data-field="years" style={{ color: accent, fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap" }}>{date}</span> : null}
            </div>
            {item.company ? <div data-field="company" style={{ marginTop: 3, color: muted, fontSize: 12.2 }}>{item.company}</div> : null}
            {highlights.length ? (
              <ul className="np-list" style={{ color: dark ? "#d1d5db" : config.text, fontSize: 12.2, marginTop: 7 }}>
                {highlights.map((highlight, highlightIndex) => <li key={highlightIndex}>{highlight}</li>)}
              </ul>
            ) : null}
          </>
        );

        const cardBorder = `1px solid ${withAlpha(accent, "33")}`;
        const ledgerBorder = `1px solid ${withAlpha(accent, "44")}`;
        const itemBorderStyle = mode === "ledger"
          ? {
            borderTop: "none",
            borderRight: "none",
            borderBottom: ledgerBorder,
            borderLeft: "none",
          }
          : {
            borderTop: cardBorder,
            borderRight: cardBorder,
            borderBottom: cardBorder,
            borderLeft: cardBorder,
          };

        if (mode === "timeline") {
          return (
            <article key={`${item.company}-${index}`} className="np-item cv-group" data-section="experienceItem" style={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", gap: 10 }}>
              <div aria-hidden="true" style={{ display: "grid", justifyItems: "center" }}>
                <span style={{ width: 13, height: 13, borderRadius: "50%", background: accent, marginTop: 5 }} />
                <span style={{ width: 2, minHeight: 72, background: withAlpha(accent, "55"), marginTop: 4 }} />
              </div>
              <div style={{ border: `1px solid ${withAlpha(accent, "44")}`, borderRadius: 16, padding: "11px 14px", background: dark ? withAlpha("#ffffff", "10") : "#ffffff" }}>{body}</div>
            </article>
          );
        }

        return (
          <article
            key={`${item.company}-${index}`}
            className="np-item cv-group"
            data-section="experienceItem"
            style={{
              display: numbered ? "grid" : "block",
              gridTemplateColumns: numbered ? "42px minmax(0, 1fr)" : undefined,
              gap: numbered ? 12 : undefined,
              padding: mode === "ledger" ? "10px 0" : "12px 14px",
              ...itemBorderStyle,
              borderRadius: mode === "ledger" ? 0 : 16,
              background: mode === "card" ? (dark ? withAlpha("#ffffff", "0f") : "#ffffff") : "transparent",
            }}
          >
            {numbered ? <div style={{ color: accent, fontSize: 24, fontWeight: 900 }}>{String(index + 1).padStart(2, "0")}</div> : null}
            <div>{body}</div>
          </article>
        );
      })}
    </div>
  );
}

function EducationList({ items, config, compact = false, dark = false }) {
  if (!items.length) return null;
  const accent = config.dynamicAccent || config.accent;
  const color = dark ? "#ffffff" : config.text;
  const muted = dark ? "#cbd5e1" : config.muted;

  return (
    <div style={{ display: "grid", gap: compact ? 5 : 9 }}>
      {items.map((item, index) => {
        const title = clean(item.degree || item.field || "Education");
        const institution = clean(item.institution);
        const date = formatDateRange(item.from, item.to, item.years);
        return (
          <article key={`${title}-${index}`} data-section="educationItem" className="np-item" style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 10 }}>
            <div data-field="degree" style={{ color, fontSize: compact ? 12.4 : 13.5, fontWeight: 900 }}>{title}</div>
            {[institution, item.field && item.field !== item.degree ? item.field : "", date, item.score].filter(Boolean).map((row, rowIndex) => (
              <div key={rowIndex} style={{ color: rowIndex === 3 ? accent : muted, fontSize: compact ? 11.2 : 12, marginTop: 1 }}>
                {row}
              </div>
            ))}
          </article>
        );
      })}
    </div>
  );
}

function ReactList({ items, dataField = "achievements", color }) {
  const normalizedItems = items
    .map((item) => {
      if (typeof item === "string" || React.isValidElement(item)) return item;
      if (!item || typeof item !== "object") return "";
      return item.text || item.title || item.description || item.name || item.label || item.value || "";
    })
    .filter((item) => React.isValidElement(item) || clean(item));

  if (!normalizedItems.length) return null;
  return (
    <ul className="np-list" style={{ color, fontSize: 12.3 }}>
      {normalizedItems.map((item, index) => <li key={index} data-field={dataField}>{item}</li>)}
    </ul>
  );
}

function ProjectList({ items, config }) {
  if (!items.length) return null;
  const accent = config.dynamicAccent || config.accent;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {items.map((item, index) => (
        <article key={`${item.title}-${index}`} data-section="projectItem" className="np-item" style={{ border: `1px solid ${withAlpha(accent, "3f")}`, borderRadius: 14, padding: "9px 11px" }}>
          <div data-field="project" style={{ color: config.text, fontWeight: 900, fontSize: 13.2 }}>{clean(item.title) || "Project"}</div>
          {item.subtitle ? <div style={{ color: config.muted, fontSize: 11.5, marginTop: 2 }}>{item.subtitle}</div> : null}
          {Array.isArray(item.details) && item.details.length ? (
            <ul className="np-list" style={{ color: config.text, fontSize: 11.7, marginTop: 6 }}>
              {item.details.slice(0, 3).map((detail, detailIndex) => (
                <li key={detailIndex}><ResumeRichTextInline html={detail} dataField="project" /></li>
              ))}
            </ul>
          ) : null}
        </article>
      ))}
    </div>
  );
}

function MetricTiles({ model, level, config, dark = false }) {
  const accent = config.dynamicAccent || config.accent;
  const metricItems = level === "experienced"
    ? [
        model.totalExperience ? { label: "Experience", value: `${model.totalExperience}+` } : null,
        model.currentCompany ? { label: "Current", value: model.currentCompany } : null,
        model.previousCompanies.length ? { label: "Previous", value: String(model.previousCompanies.length) } : null,
      ].filter(Boolean)
    : [
        model.education.length ? { label: "Education", value: String(model.education.length) } : null,
        model.projects.length ? { label: "Projects", value: String(model.projects.length) } : null,
        model.certifications.length ? { label: "Certs", value: String(model.certifications.length) } : null,
      ].filter(Boolean);

  if (!metricItems.length) return null;

  return (
    <div className="np-grid-two">
      {metricItems.map((item) => (
        <div key={item.label} style={{ border: `1px solid ${withAlpha(accent, "44")}`, borderRadius: 16, padding: 12, color: dark ? "#ffffff" : config.text }}>
          <div style={{ color: accent, fontSize: 11, fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase" }}>{item.label}</div>
          <div style={{ marginTop: 5, fontSize: 18, fontWeight: 900, lineHeight: 1.15 }}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function MainSections({ model, level, config, dark = false, skillMode = "chips", experienceMode = "card", titleMap = {}, skip = {} }) {
  const color = dark ? "#f8fafc" : config.text;
  const aboutTitle = titleMap.about || (level === "experienced" ? "About Me" : "About Me");
  const objectiveTitle = titleMap.objective || "Career Objective";
  const experienceTitle = titleMap.experience || (level === "experienced" ? "Experience" : "Projects & Internships");
  const fresherItems = [...model.projects, ...model.internships];
  const certificationItems = level === "experienced" ? model.certifications : [...model.certifications, ...model.training];
  const leadershipItems = [...model.leadership, ...model.domains];

  return (
    <>
      {!skip.about && clean(model.about) ? (
        <Section title={aboutTitle} config={config} dark={dark} sectionKey="summary">
          <TextBlock text={model.about} color={color} />
        </Section>
      ) : null}
      {!skip.objective && clean(model.objective) ? (
        <Section title={objectiveTitle} config={config} dark={dark} sectionKey="objective">
          <TextBlock text={model.objective} color={color} dataField="objective" />
        </Section>
      ) : null}
      {!skip.skills && model.skills.length ? (
        <Section title="Skills" config={config} dark={dark} sectionKey="skills">
          <Skills items={model.skills} config={config} mode={skillMode} dark={dark} />
        </Section>
      ) : null}
      {level === "experienced" && model.experiences.length ? (
        <Section title={experienceTitle} config={config} dark={dark} sectionKey="experience">
          <ExperienceList items={model.experiences} config={config} mode={experienceMode} dark={dark} numbered={experienceMode === "ledger"} />
        </Section>
      ) : null}
      {level === "experienced" && model.projects.length ? (
        <Section title="Projects" config={config} dark={dark} sectionKey="projects">
          <ProjectList items={model.projects} config={config} />
        </Section>
      ) : null}
      {level === "fresher" && fresherItems.length ? (
        <Section title={experienceTitle} config={config} dark={dark} sectionKey="projects">
          <ProjectList items={model.projects} config={config} />
          <ReactList items={model.internships} dataField="internship" color={color} />
        </Section>
      ) : null}
      {model.education.length ? (
        <Section title="Education" config={config} dark={dark} sectionKey="education">
          <EducationList items={model.education} config={config} dark={dark} />
        </Section>
      ) : null}
      {level === "experienced" ? (
        <>
          {certificationItems.length ? (
            <Section title="Professional Certifications" config={config} dark={dark} sectionKey="certifications">
              <ReactList items={certificationItems} dataField="certification" color={color} />
            </Section>
          ) : null}
          {leadershipItems.length ? (
            <Section title="Leadership & Domain" config={config} dark={dark} sectionKey="domain">
              <ReactList items={leadershipItems} dataField="leadership" color={color} />
            </Section>
          ) : null}
        </>
      ) : (
        <>
          {certificationItems.length ? (
            <Section title="Certifications & Training" config={config} dark={dark} sectionKey="certifications">
              <ReactList items={certificationItems} dataField="certification" color={color} />
            </Section>
          ) : null}
        </>
      )}
      {model.achievements.length ? (
        <Section title="Achievements" config={config} dark={dark} sectionKey="achievements">
          <ReactList items={model.achievements} color={color} />
        </Section>
      ) : null}
      {model.awards.length ? (
        <Section title="Awards" config={config} dark={dark} sectionKey="awards">
          <ReactList items={model.awards} color={color} />
        </Section>
      ) : null}
      {model.publications.length ? (
        <Section title="Publications" config={config} dark={dark} sectionKey="publications">
          <ReactList items={model.publications} color={color} />
        </Section>
      ) : null}
      {model.customSections.length ? (
        <Section title="Additional Information" config={config} dark={dark} sectionKey="customSections">
          <ReactList items={model.customSections} color={color} />
        </Section>
      ) : null}
    </>
  );
}

function SidebarSections({ model, level, config, dark = true, skillMode = "bars" }) {
  const color = dark ? "#f8fafc" : config.text;
  const sideItems = level === "experienced" ? model.domains : model.training;
  return (
    <>
      {model.contacts.length || model.socials.length ? (
        <Section title="Contact" config={config} dark={dark}>
          <ContactStrip model={model} config={config} dark={dark} style={{ display: "grid", gap: 8 }} />
        </Section>
      ) : null}
      {model.skills.length ? (
        <Section title="Skills" config={config} dark={dark} sectionKey="skills">
          <Skills items={model.skills} config={config} mode={skillMode} dark={dark} />
        </Section>
      ) : null}
      {sideItems.length ? (
        <Section title={level === "experienced" ? "Domain" : "Training"} config={config} dark={dark}>
          <ReactList items={sideItems} color={color} />
        </Section>
      ) : null}
      {model.languages.length ? (
        <Section title="Languages" config={config} dark={dark}>
          <ReactList items={model.languages} dataField="language" color={color} />
        </Section>
      ) : null}
    </>
  );
}

function renderLayout(config, model, level) {
  if (config?.legacyPrototypeLayout) return renderLegacyLayout(config, model, level);
  return renderDistinctPrototypeLayout(config, model, level);
}

function renderLegacyLayout(config, model, level) {
  const accent = config.dynamicAccent || config.accent;

  switch (config.layout) {
    case "orbit":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 30, padding: "34px 48px", position: "relative" }}>
            <div style={{ position: "absolute", right: 44, top: 42 }}><Avatar model={model} config={config} size={106} /></div>
            <Header model={model} config={config} style={{ paddingRight: 142, marginBottom: 22 }} />
            <Section title="About Me" config={config} style={{ background: config.dark, borderRadius: 22, padding: "16px 22px", marginBottom: 18 }} dark sectionKey="summary">
              <TextBlock text={model.about} color="#ffffff" />
            </Section>
            <MainSections model={model} level={level} config={config} skillMode="chips" experienceMode="card" skip={{ about: true }} />
          </Paper>
        </Shell>
      );
    case "bento":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 32, border: `1px solid ${config.border}`, background: "rgba(255,255,255,0.7)" }}>
            <Header model={model} config={config} style={{ marginBottom: 22 }} />
            <div className="np-grid-two">
              <div style={{ display: "grid", gap: 14 }}>
                <Section title="About Me" config={config} style={bentoCard(config)} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section>
                {model.objective ? <Section title="Career Objective" config={config} style={bentoCard(config)} sectionKey="objective"><TextBlock text={model.objective} color={config.text} dataField="objective" /></Section> : null}
              </div>
              <div style={{ display: "grid", gap: 14 }}>
                <div style={{ ...bentoCard(config), background: config.dark, color: "#ffffff" }}><MetricTiles model={model} level={level} config={config} dark /></div>
                <Section title="Skills" config={config} style={bentoCard(config)} sectionKey="skills"><Skills items={model.skills} config={config} mode="chips" /></Section>
              </div>
            </div>
            <div style={{ marginTop: 14 }}><MainSections model={model} level={level} config={config} skillMode="chips" experienceMode="card" skip={{ about: true, objective: true, skills: true }} /></div>
          </Paper>
        </Shell>
      );
    case "ledger":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ border: `2px solid ${accent}`, padding: "34px 38px" }}>
            <div style={{ color: accent, fontWeight: 900, fontSize: 14, marginBottom: 30 }}>RESUME LEDGER</div>
            <Header model={model} config={config} style={{ borderBottom: `2px solid ${accent}`, paddingBottom: 18, marginBottom: 22 }} />
            <MainSections model={model} level={level} config={config} skillMode="inline" experienceMode="ledger" />
          </Paper>
        </Shell>
      );
    case "rail":
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <div className="np-main-grid" data-resume-root="true" style={{ minHeight: "100%", gridTemplateColumns: "218px minmax(0, 1fr)", background: config.paper }}>
            <aside style={{ background: accent, color: "#ffffff", padding: "34px 32px" }}>
              <Avatar model={model} config={config} size={92} dark style={{ background: "#fff3ed", color: accent, marginBottom: 24 }} />
              <h1 data-field="name" style={{ margin: "0 0 8px", color: "#ffffff", fontSize: 28, lineHeight: 1.1 }}>{model.name}</h1>
              <div data-field="roleTitle" style={{ color: "#ffe1d7", fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 34 }}>{model.role}</div>
              <SidebarSections model={model} level={level} config={config} dark skillMode="bars" />
            </aside>
            <main style={{ padding: "42px 38px" }}>
              <Header model={model} config={config} style={{ marginBottom: 20 }} />
              <MainSections model={model} level={level} config={config} skillMode="chips" experienceMode="card" titleMap={{ about: "About Me" }} />
            </main>
          </div>
        </Shell>
      );
    case "neon":
      return (
        <Shell config={config} style={{ background: config.dark }}>
          <Paper config={config} style={{ border: `1px solid ${withAlpha(accent, "66")}`, background: "#050607", color: "#f8fafc", padding: 34 }}>
            <div style={{ borderTop: `2px solid ${accent}`, paddingTop: 24, display: "grid", gridTemplateColumns: "170px minmax(0, 1fr)", gap: 30 }}>
              <aside>
                <Avatar model={model} config={config} size={116} dark style={{ marginBottom: 30 }} />
                <SidebarSections model={model} level={level} config={config} dark skillMode="bars" />
              </aside>
              <main>
                <Header model={model} config={config} dark style={{ marginBottom: 26 }} />
                <MainSections model={model} level={level} config={config} dark skillMode="chips" experienceMode="timeline" />
              </main>
            </div>
          </Paper>
        </Shell>
      );
    case "timeline":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 32, padding: "34px 10px" }}>
            <div style={{ background: config.dark, color: "#ffffff", borderRadius: 26, padding: "24px 28px", marginBottom: 24 }}>
              <Header model={model} config={config} dark />
            </div>
            <MainSections model={model} level={level} config={config} skillMode="square" experienceMode="timeline" />
          </Paper>
        </Shell>
      );
    case "editorial":
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <Paper config={config} style={{ border: "none", background: config.page, padding: "54px 58px" }}>
            <Header model={model} config={config} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 220px", gap: 30, marginBottom: 34 }} />
            <Section title="About Me" config={config} style={{ marginBottom: 24 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} style={{ fontFamily: config.font, fontSize: 17 }} /></Section>
            <MainSections model={model} level={level} config={config} skillMode="inline" experienceMode="card" skip={{ about: true }} />
          </Paper>
        </Shell>
      );
    case "product":
      return (
        <Shell config={config}>
          <Paper
            config={config}
            style={{
              borderRadius: 34,
              padding: 32,
              WebkitBoxDecorationBreak: "slice",
              boxDecorationBreak: "slice",
            }}
          >
            <div style={{ background: accent, borderRadius: 28, padding: "24px 28px", color: "#ffffff", marginBottom: 26 }}>
              <Header model={model} config={config} dark />
            </div>
            <div className="np-grid-two">
              <Section title="About Me" config={config} style={productCard(config)} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section>
              {model.objective ? <Section title="Career Objective" config={config} style={productCard(config)} sectionKey="objective"><TextBlock text={model.objective} color={config.text} dataField="objective" /></Section> : null}
            </div>
            <div style={{ marginTop: 14 }}><MainSections model={model} level={level} config={config} skillMode="chips" experienceMode="card" skip={{ about: true, objective: true }} /></div>
          </Paper>
        </Shell>
      );
    case "slate":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ padding: 0, border: "none" }}>
            <div style={{ background: config.dark, padding: "34px 36px", color: "#ffffff" }}><Header model={model} config={config} dark /></div>
            <main style={{ padding: "34px 36px" }}>
              <div className="np-grid-two">
                <Section title="About Me" config={config} style={slateCard(config)} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section>
                <Section title="Skills" config={config} style={slateCard(config)} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section>
              </div>
              <div style={{ marginTop: 14 }}><MainSections model={model} level={level} config={config} skillMode="chips" experienceMode="card" skip={{ about: true, skills: true }} /></div>
            </main>
          </Paper>
        </Shell>
      );
    case "founder":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 28, padding: 28 }}>
            <div style={{ background: config.dark, color: "#ffffff", borderRadius: 24, padding: "24px 30px", marginBottom: 24 }}><Header model={model} config={config} dark /></div>
            <Section title="01 Value Proposition" config={config} sectionKey="summary">
              <TextBlock text={model.about || model.objective} color={config.text} style={{ fontSize: 18, fontWeight: 800 }} />
            </Section>
            <MainSections model={model} level={level} config={config} skillMode="square" experienceMode="card" skip={{ about: true }} />
          </Paper>
        </Shell>
      );
    case "ivory":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ border: `1px solid ${config.border}`, padding: "44px 42px" }}>
            <Header model={model} config={config} style={{ marginBottom: 34 }} />
            <MainSections model={model} level={level} config={config} skillMode="inline" experienceMode="ledger" />
          </Paper>
        </Shell>
      );
    case "graphite":
      return (
        <Shell config={config}>
          <div className="np-main-grid" data-resume-root="true" style={{ minHeight: "100%", gridTemplateColumns: "250px minmax(0, 1fr)", background: config.paper }}>
            <aside style={{ background: config.dark, color: "#ffffff", padding: "42px 36px" }}>
              <Avatar model={model} config={config} size={112} dark style={{ marginBottom: 34, background: "#f9fafb", color: config.dark }} />
              <h1 data-field="name" style={{ color: "#ffffff", fontSize: 30, lineHeight: 1.1, margin: 0 }}>{model.name}</h1>
              <div data-field="roleTitle" style={{ color: "#9ca3af", fontSize: 11.5, letterSpacing: "0.15em", textTransform: "uppercase", margin: "10px 0 40px" }}>{model.role}</div>
              <SidebarSections model={model} level={level} config={config} dark skillMode="chips" />
            </aside>
            <main style={{ padding: "42px 38px" }}>
              <MainSections model={model} level={level} config={config} skillMode="inline" experienceMode="card" />
            </main>
          </div>
        </Shell>
      );
    case "sky":
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <Paper config={config} style={{ border: "none", padding: 0 }}>
            <div style={{ background: "#e0f2fe", padding: "36px 48px" }}><Header model={model} config={config} layout="avatar-left" /></div>
            <main style={{ padding: "36px 48px" }}><MainSections model={model} level={level} config={config} skillMode="chips" experienceMode="timeline" /></main>
          </Paper>
        </Shell>
      );
    case "luxury":
      return (
        <Shell config={config} style={{ background: config.paper, padding: 0 }}>
          <Paper config={config} style={{ borderTop: `12px solid ${accent}`, borderRight: "none", borderBottom: "none", borderLeft: "none", padding: "40px 36px" }}>
            <Header model={model} config={config} style={{ marginBottom: 28 }} />
            <Section title="About Me" config={config} style={{ background: config.dark, padding: "16px 20px", marginBottom: 20 }} dark sectionKey="summary">
              <TextBlock text={model.about} color={config.paper} />
            </Section>
            <MainSections model={model} level={level} config={config} skillMode="inline" experienceMode="card" skip={{ about: true }} />
          </Paper>
        </Shell>
      );
    case "ats":
    default:
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <Paper config={config} style={{ border: "none", padding: "40px 48px" }}>
            <Header model={model} config={config} style={{ borderBottom: `1px solid ${config.border}`, paddingBottom: 18, marginBottom: 26 }} />
            <MainSections model={model} level={level} config={config} skillMode="inline" experienceMode="ledger" />
          </Paper>
        </Shell>
      );
  }
}

function renderDistinctPrototypeLayout(config, model, level) {
  const accent = config.dynamicAccent || config.accent;
  const isExperienced = level === "experienced";
  const hasAbout = Boolean(clean(model.about));
  const hasObjective = Boolean(clean(model.objective));
  const hasExperience = isExperienced && model.experiences.length > 0;
  const hasFresherWork = !isExperienced && (model.projects.length > 0 || model.internships.length > 0);
  const workTitle = isExperienced ? "Experience" : "Projects & Internships";
  const heroText = model.about || model.objective;
  const careerText = model.objective;
  const certificationItems = isExperienced ? model.certifications : [...model.certifications, ...model.training];
  const leadershipItems = [...model.leadership, ...model.domains];

  switch (config.layout) {
    case "orbit":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 30, padding: "34px 48px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", right: 42, top: 44 }}>
              <Avatar model={model} config={config} size={106} />
            </div>
            <header data-section="header" style={{ paddingRight: 150, marginBottom: 28 }}>
              <h1 data-field="name" style={{ margin: 0, color: config.text, fontSize: 42, lineHeight: 1.02, fontWeight: 900 }}>{model.name}</h1>
              <div data-field="roleTitle" style={{ marginTop: 10, color: accent, fontSize: 15, letterSpacing: "0.16em", textTransform: "uppercase" }}>{model.role}</div>
              <ContactStrip model={model} config={config} style={{ marginTop: 20 }} />
            </header>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 238px", gap: 18, marginBottom: 18, alignItems: "stretch" }}>
              {hasAbout ? (
                <Section title="About Me" config={config} style={{ background: config.dark, borderRadius: 22, padding: "18px 24px", margin: 0, minHeight: 112 }} dark sectionKey="summary">
                  <TextBlock text={model.about} color="#ffffff" style={{ fontSize: 14, lineHeight: 1.5 }} />
                </Section>
              ) : null}
              <div style={{ border: `1px solid ${config.border}`, borderRadius: 22, padding: "16px 18px", display: "grid", gap: 10 }}>
                {[
                  model.totalExperience ? ["Experience", `${model.totalExperience}+`] : null,
                  model.currentCompany ? ["Current", model.currentCompany] : null,
                  model.previousCompanies.length ? ["Previous", `${model.previousCompanies.length} companies`] : null,
                ].filter(Boolean).map(([label, value]) => (
                  <div key={label} style={{ border: `1px solid ${withAlpha(accent, "33")}`, borderRadius: 14, padding: "9px 11px", minWidth: 0 }}>
                    <div style={{ color: accent, fontSize: 10, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase" }}>{label}</div>
                    <div style={{ color: config.text, fontSize: 14, fontWeight: 900, lineHeight: 1.2, marginTop: 4, overflowWrap: "break-word" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 18, marginBottom: 18, alignItems: "stretch" }}>
              {(hasExperience || hasFresherWork) ? (
                <Section title={workTitle} config={config} style={{ ...productCard(config), height: "100%" }} sectionKey={isExperienced ? "experience" : "projects"}>
                  {hasExperience ? <ExperienceList items={model.experiences.slice(0, 1)} config={config} mode="card" /> : null}
                  {hasFresherWork ? <><ProjectList items={model.projects.slice(0, 1)} config={config} /><ReactList items={model.internships.slice(0, 1)} dataField="internship" color={config.text} /></> : null}
                </Section>
              ) : null}
              <div style={{ display: "grid", gap: 12, gridAutoRows: "minmax(0, 1fr)", alignSelf: "stretch", minHeight: 0 }}>
                {model.currentCompany ? <Section title="Current Company" config={config} style={{ ...productCard(config), height: "100%", minHeight: 0 }}><TextBlock text={model.currentCompany} color={config.text} style={{ fontWeight: 900, fontSize: 15 }} /></Section> : null}
                {model.education.length ? <Section title="Education" config={config} style={{ ...productCard(config), height: "100%", minHeight: 0 }} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
                {model.previousCompanies.length ? <Section title="Previous Companies" config={config} style={{ ...productCard(config), height: "100%", minHeight: 0 }}><ReactList items={model.previousCompanies.slice(0, 4)} color={config.text} /></Section> : null}
              </div>
            </div>
            {model.projects.length ? (
              <Section title="Projects" config={config} style={{ marginBottom: 18 }} sectionKey="projects">
                <ProjectList items={model.projects} config={config} />
              </Section>
            ) : null}
            {model.skills.length ? (
              <Section title="Skills" config={config} style={{ marginBottom: 18 }} sectionKey="skills">
                <Skills items={model.skills} config={config} mode="chips" />
              </Section>
            ) : null}
            {model.achievements.length ? (
              <Section title="Achievements" config={config} style={productCard(config)} sectionKey="achievements">
                <ReactList items={model.achievements} color={config.text} />
              </Section>
            ) : null}
            {model.languages.length ? (
              <Section title="Languages" config={config} style={{ marginTop: 18 }} sectionKey="languages">
                <ReactList items={model.languages} dataField="language" color={config.text} />
              </Section>
            ) : null}
            {model.certifications.length ? (
              <Section title="Certifications" config={config} style={{ marginTop: 18 }} sectionKey="certifications">
                <ReactList items={model.certifications} dataField="certification" color={config.text} />
              </Section>
            ) : null}
            {model.awards.length ? (
              <Section title="Awards" config={config} style={{ marginTop: 18 }} sectionKey="awards">
                <ReactList items={model.awards} color={config.text} />
              </Section>
            ) : null}
            {model.publications.length ? (
              <Section title="Publications" config={config} style={{ marginTop: 18 }} sectionKey="publications">
                <ReactList items={model.publications} color={config.text} />
              </Section>
            ) : null}
            {model.customSections.length ? (
              <Section title="Additional Information" config={config} style={{ marginTop: 18 }} sectionKey="customSections">
                <ReactList items={model.customSections} color={config.text} />
              </Section>
            ) : null}
          </Paper>
        </Shell>
      );
    case "bento":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 32, border: `1px solid ${config.border}`, background: "rgba(255,255,255,0.72)", padding: 30 }}>
            <Header model={model} config={config} layout="split" style={{ marginBottom: 22 }} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gridAutoRows: "minmax(86px, auto)", gap: 14 }}>
              {hasAbout ? <Section title="About Me" config={config} style={{ ...bentoCard(config), gridColumn: "span 7", gridRow: "span 2" }} sectionKey="summary"><TextBlock text={model.about} color={config.text} style={{ fontSize: 14 }} /></Section> : null}
              <div style={{ ...bentoCard(config), background: config.dark, color: "#ffffff", gridColumn: "span 5", gridRow: "span 2" }}>
                <MetricTiles model={model} level={level} config={config} dark />
              </div>
              {hasObjective ? <Section title="Career Objective" config={config} style={{ ...bentoCard(config), gridColumn: "span 4" }} sectionKey="objective"><TextBlock text={model.objective} color={config.text} dataField="objective" /></Section> : null}
              {model.skills.length ? <Section title="Skills" config={config} style={{ ...bentoCard(config), gridColumn: "span 8" }} sectionKey="skills"><Skills items={model.skills} config={config} mode="chips" /></Section> : null}
              {(hasExperience || hasFresherWork) ? (
                <Section title={workTitle} config={config} style={{ ...bentoCard(config), gridColumn: "span 12" }} sectionKey={isExperienced ? "experience" : "projects"}>
                  {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                  {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
                </Section>
              ) : null}
              {model.education.length ? <Section title="Education" config={config} style={{ ...bentoCard(config), gridColumn: "span 5" }} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} style={{ ...bentoCard(config), gridColumn: "span 7" }} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "ledger":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ border: `2px solid ${accent}`, padding: "34px 38px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 24, alignItems: "start", paddingBottom: 22, borderBottom: `2px solid ${accent}` }}>
              <div>
                <div style={{ color: accent, fontWeight: 900, fontSize: 14, marginBottom: 30 }}>RESUME LEDGER</div>
                <h1 data-field="name" style={{ margin: 0, color: config.text, fontSize: 46, lineHeight: 1.02, fontWeight: 900 }}>{model.name}</h1>
                <div data-field="roleTitle" style={{ display: "inline-block", background: accent, color: "#ffffff", padding: "7px 16px", marginTop: 12, fontSize: 13, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>{model.role}</div>
                <ContactStrip model={model} config={config} style={{ marginTop: 20 }} />
              </div>
              <Avatar model={model} config={config} size={92} />
            </div>
            {hasAbout ? <Section title="About Me" config={config} style={{ marginTop: 24, marginBottom: 26 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} style={{ marginBottom: 26 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="ledger" numbered /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            {model.skills.length ? (
              <Section title="Skills" config={config} style={{ borderTop: `1px solid ${accent}`, paddingTop: 18, marginBottom: 24 }} sectionKey="skills">
                <Skills items={model.skills} config={config} mode="inline" />
              </Section>
            ) : null}
            <div className="np-grid-two">
              {model.education.length ? <Section title="Education" config={config} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "rail":
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <div className="np-main-grid" data-resume-root="true" style={{ minHeight: "100%", gridTemplateColumns: "218px minmax(0, 1fr)", gap: 0, background: config.paper }}>
            <aside style={{ background: accent, color: "#ffffff", padding: "34px 32px" }}>
              <Avatar model={model} config={config} size={92} dark style={{ background: "#fff3ed", color: accent, margin: "0 auto 24px" }} />
              <h1 data-field="name" style={{ margin: "0 0 8px", color: "#ffffff", fontSize: 28, lineHeight: 1.1 }}>{model.name}</h1>
              <div data-field="roleTitle" style={{ color: "#ffe1d7", fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 34 }}>{model.role}</div>
              <SidebarSections model={model} level={level} config={config} dark skillMode="bars" />
            </aside>
            <main style={{ padding: "42px 38px" }}>
              <h2 style={{ margin: "0 0 10px", color: config.text, fontSize: 36, lineHeight: 1.08, fontWeight: 900 }}>Product UI Engineer</h2>
              <TextBlock text={heroText} color={config.muted} />
              {hasAbout ? <Section title="About Me" config={config} style={{ ...productCard(config), marginTop: 28, marginBottom: 28 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
              {(hasExperience || hasFresherWork) ? (
                <Section title={workTitle} config={config} sectionKey={isExperienced ? "experience" : "projects"}>
                  {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                  {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
                </Section>
              ) : null}
              <div className="np-grid-two" style={{ marginTop: 20 }}>
                {model.education.length ? <Section title="Education" config={config} style={productCard(config)} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
                {model.achievements.length ? <Section title="Achievements" config={config} style={productCard(config)} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
              </div>
            </main>
          </div>
        </Shell>
      );
    case "neon":
      return (
        <Shell config={config} style={{ background: config.dark }}>
          <Paper config={config} style={{ border: `1px solid ${withAlpha(accent, "66")}`, background: "#050607", color: "#f8fafc", padding: 34, position: "relative", overflow: "hidden" }}>
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.07), transparent 28%, transparent 74%, rgba(255,255,255,0.06))" }} />
            <div aria-hidden="true" style={{ position: "absolute", left: 34, top: 86, width: 300, borderTop: `2px solid ${accent}` }} />
            <div style={{ position: "relative", display: "grid", gridTemplateColumns: "198px minmax(0, 1fr)", gap: 34 }}>
              <aside style={{ paddingTop: 22 }}>
                <Avatar model={model} config={config} size={116} dark style={{ marginBottom: 36, boxShadow: `0 0 0 1px ${accent}, 0 0 26px ${withAlpha(accent, "55")}` }} />
                <Section title="Expertise" config={config} dark style={{ marginBottom: 34 }} sectionKey="skills">
                  <Skills items={model.skills} config={config} mode="bars" dark />
                </Section>
                {model.education.length ? (
                  <Section title="Education" config={config} dark style={{ marginBottom: 34 }} sectionKey="education">
                    <EducationList items={model.education} config={config} dark />
                  </Section>
                ) : null}
                <Section title="Contact" config={config} dark style={{ marginBottom: 34 }}>
                  <ContactStrip model={model} config={config} dark style={{ display: "grid", gap: 9 }} />
                </Section>
                {model.languages.length ? <Section title="Language" config={config} dark><ReactList items={model.languages} dataField="language" color="#cbd5e1" /></Section> : null}
              </aside>
              <main>
                <header data-section="header" style={{ marginBottom: 58 }}>
                  <h1 data-field="name" style={{ margin: 0, color: "#ffffff", fontSize: 42, lineHeight: 1.04, fontWeight: 900 }}>{model.name}</h1>
                  <div data-field="roleTitle" style={{ color: accent, fontSize: 15, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 8 }}>{model.role}</div>
                  <ContactStrip model={model} config={config} dark style={{ marginTop: 16, border: `1px solid ${accent}`, padding: "7px 12px", display: "flex", justifyContent: "space-between" }} />
                </header>
                {hasAbout ? (
                  <Section title="About Me" config={config} dark style={{ marginBottom: 40 }} sectionKey="summary">
                    <TextBlock text={model.about} color="#d1d5db" />
                  </Section>
                ) : null}
                {(hasExperience || hasFresherWork) ? (
                  <Section title={isExperienced ? "Work Experience" : "Projects & Training"} config={config} dark sectionKey={isExperienced ? "experience" : "projects"}>
                    {hasExperience ? (
                      <div style={{ display: "grid", gap: 24 }}>
                        {model.experiences.map((item, index) => {
                          const date = formatDateRange(item.startDate, item.endDate, item.years);
                          return (
                            <article key={`${item.company}-${index}`} className="np-item" style={{ display: "grid", gridTemplateColumns: "18px minmax(0, 1fr)", gap: 16 }}>
                              <span aria-hidden="true" style={{ width: 0, height: 0, borderTop: "7px solid transparent", borderBottom: "7px solid transparent", borderLeft: `11px solid ${accent}`, marginTop: 4 }} />
                              <div>
                                <h3 data-field="jobRole" style={{ margin: 0, color: "#ffffff", fontSize: 17, fontWeight: 900 }}>{item.role || "Role"}</h3>
                                <div data-field="company" style={{ color: "#94a3b8", fontSize: 12.2, marginTop: 5 }}>{[item.company, date].filter(Boolean).join(" | ")}</div>
                                <ul className="np-list" style={{ color: "#d1d5db", fontSize: 12, marginTop: 10 }}>
                                  {getExperienceHighlights(item, 2).map((highlight, highlightIndex) => <li key={highlightIndex}>{highlight}</li>)}
                                </ul>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ) : null}
                    {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color="#d1d5db" /></> : null}
                  </Section>
                ) : null}
              </main>
            </div>
            {model.achievements.length ? (
              <Section title="Achievements" config={config} dark style={{ position: "relative", marginTop: 34 }} sectionKey="achievements">
                <ReactList items={model.achievements} color="#d1d5db" />
              </Section>
            ) : null}
          </Paper>
        </Shell>
      );
    case "timeline":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 32, padding: "34px 40px" }}>
            <div style={{ background: config.dark, color: "#ffffff", borderRadius: 26, padding: "24px 28px", marginBottom: 34 }}>
              <Header model={model} config={config} dark layout="split" />
            </div>
            {hasAbout ? <Section title="About Me" config={config} style={{ marginBottom: 28 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
            {model.skills.length ? <Section title="Skills" config={config} style={{ marginBottom: 38 }} sectionKey="skills"><Skills items={model.skills} config={config} mode="square" /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} style={{ marginBottom: 26 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? (
                  <div style={{ position: "relative", display: "grid", gap: 24, paddingLeft: 56 }}>
                    <span aria-hidden="true" style={{ position: "absolute", left: 18, top: 14, bottom: 14, width: 3, background: accent, borderRadius: 999 }} />
                    {model.experiences.map((item, index) => {
                      const date = formatDateRange(item.startDate, item.endDate, item.years);
                      return (
                        <article key={`${item.company}-${index}`} className="np-item" style={{ position: "relative", background: "#f8fafc", border: `1px solid ${config.border}`, borderRadius: 18, padding: "16px 20px" }}>
                          <span aria-hidden="true" style={{ position: "absolute", left: -44, top: 22, width: 20, height: 20, borderRadius: "50%", background: accent, boxShadow: `0 0 0 7px ${withAlpha(accent, "20")}` }} />
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 12, alignItems: "baseline" }}>
                            <h3 data-field="jobRole" style={{ margin: 0, color: config.text, fontSize: 17, lineHeight: 1.2, fontWeight: 900 }}>{item.role || "Role"}</h3>
                            {date ? <span data-field="years" style={{ color: accent, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>{date}</span> : null}
                          </div>
                          {item.company ? <div data-field="company" style={{ color: config.muted, fontSize: 12.5, marginTop: 5 }}>{item.company}</div> : null}
                          <ul className="np-list" style={{ color: config.text, fontSize: 12.2, marginTop: 10 }}>
                            {getExperienceHighlights(item, 3).map((highlight, highlightIndex) => <li key={highlightIndex}>{highlight}</li>)}
                          </ul>
                        </article>
                      );
                    })}
                  </div>
                ) : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            <div className="np-grid-two">
              {model.education.length ? <Section title="Education" config={config} style={slateCard(config)} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} style={slateCard(config)} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "editorial":
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <Paper config={config} style={{ border: "none", background: config.page, padding: "54px 58px" }}>
            <header data-section="header" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 220px", gap: 30, alignItems: "start", paddingBottom: 34, borderBottom: `1px solid ${config.text}` }}>
              <div>
                <h1 data-field="name" style={{ margin: 0, color: config.text, fontFamily: config.font, fontSize: 58, lineHeight: 0.98, fontWeight: 700 }}>{model.name}</h1>
                <div data-field="roleTitle" style={{ color: accent, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 14, marginBottom: 12 }}>{model.role}</div>
                <ContactStrip model={model} config={config} style={{ display: "grid", gap: 8 }} />
              </div>
              <Avatar model={model} config={config} size={84} style={{ justifySelf: "end" }} />
            </header>
            {hasAbout ? <Section title="About Me" config={config} style={{ marginTop: 34, marginBottom: 34 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} style={{ fontFamily: config.font, fontSize: 18, lineHeight: 1.55 }} /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={isExperienced ? "Selected Work" : "Selected Projects"} config={config} style={{ marginBottom: 30 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            {model.skills.length ? <Section title="Skills" config={config} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section> : null}
            <div className="np-grid-two" style={{ marginTop: 28 }}>
              {model.education.length ? <Section title="Education" config={config} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "product":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 34, padding: 32 }}>
            <div style={{ background: accent, borderRadius: 28, padding: "24px 28px", color: "#ffffff", marginBottom: 30 }}>
              <Header model={model} config={config} dark layout="split" />
            </div>
            <div className="np-grid-two" style={{ marginBottom: 30 }}>
              {hasAbout ? <Section title="About Me" config={config} style={productCard(config)} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
              {hasObjective ? <Section title="Career Objective" config={config} style={productCard(config)} sectionKey="objective"><TextBlock text={model.objective} color={config.text} dataField="objective" /></Section> : null}
            </div>
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} style={{ ...productCard(config), marginBottom: 22 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            {model.skills.length ? <Section title="Skills" config={config} style={{ ...productCard(config), marginBottom: 22 }} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section> : null}
            <div className="np-grid-two">
              {model.education.length ? <Section title="Education" config={config} style={productCard(config)} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} style={productCard(config)} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "slate":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ padding: 0, border: "none" }}>
            <div style={{ background: config.dark, padding: "34px 36px", color: "#ffffff" }}>
              <Header model={model} config={config} dark layout="split" />
            </div>
            <main style={{ padding: "34px 36px" }}>
              <div className="np-grid-two" style={{ marginBottom: 28 }}>
                {hasAbout ? <Section title="About Me" config={config} style={slateCard(config)} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
                {model.skills.length ? <Section title="Skills" config={config} style={slateCard(config)} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section> : null}
              </div>
              {(hasExperience || hasFresherWork) ? (
                <Section title={workTitle} config={config} style={{ border: `1px solid ${config.border}`, padding: 26, marginBottom: 28 }} sectionKey={isExperienced ? "experience" : "projects"}>
                  {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                  {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
                </Section>
              ) : null}
              <div className="np-grid-two">
                {model.education.length ? <Section title="Education" config={config} style={slateCard(config)} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
                {model.achievements.length ? <Section title="Achievements" config={config} style={slateCard(config)} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
              </div>
            </main>
          </Paper>
        </Shell>
      );
    case "founder":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ borderRadius: 28, padding: 28 }}>
            <div style={{ background: config.dark, color: "#ffffff", borderRadius: 24, padding: "24px 30px", marginBottom: 30 }}>
              <Header model={model} config={config} dark layout="split" />
            </div>
            <Section title="01 Value Proposition" config={config} sectionKey="summary">
              <TextBlock text={heroText} color={config.text} style={{ fontSize: 20, lineHeight: 1.35, fontWeight: 900 }} />
            </Section>
            {model.skills.length ? <Section title="Skills" config={config} style={{ ...slateCard(config), borderRadius: 20, marginTop: 26, marginBottom: 22 }} sectionKey="skills"><Skills items={model.skills} config={config} mode="square" /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} style={{ marginBottom: 22 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            <div className="np-grid-two">
              {model.education.length ? <Section title="Education" config={config} style={slateCard(config)} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} style={slateCard(config)} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "ivory":
      return (
        <Shell config={config}>
          <Paper config={config} style={{ border: `1px solid ${config.border}`, padding: "44px 42px" }}>
            <Header model={model} config={config} layout="split" style={{ borderBottom: `1px solid ${accent}`, paddingBottom: 26, marginBottom: 34 }} />
            {hasAbout ? <Section title="About Me" config={config} style={{ marginBottom: 34 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} style={{ fontFamily: config.font, fontSize: 16, lineHeight: 1.55 }} /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} style={{ marginBottom: 30 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="ledger" /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            {model.skills.length ? <Section title="Skills" config={config} style={{ background: config.page, padding: "16px 20px", marginBottom: 30 }} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section> : null}
            <div className="np-grid-two">
              {model.education.length ? <Section title="Education" config={config} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "graphite":
      return (
        <Shell config={config}>
          <div className="np-main-grid" data-resume-root="true" style={{ minHeight: "100%", gridTemplateColumns: "252px minmax(0, 1fr)", gap: 0, background: config.paper }}>
            <aside style={{ background: config.dark, color: "#ffffff", padding: "42px 36px" }}>
              <Avatar model={model} config={config} size={112} dark style={{ margin: "0 auto 34px", background: "#f9fafb", color: config.dark }} />
              <h1 data-field="name" style={{ color: "#ffffff", fontSize: 30, lineHeight: 1.1, margin: 0 }}>{model.name}</h1>
              <div data-field="roleTitle" style={{ color: "#9ca3af", fontSize: 11.5, letterSpacing: "0.15em", textTransform: "uppercase", margin: "10px 0 40px" }}>{model.role}</div>
              <SidebarSections model={model} level={level} config={config} dark skillMode="chips" />
            </aside>
            <main style={{ padding: "42px 38px" }}>
              {hasAbout ? <Section title="About Me" config={config} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
              {(hasExperience || hasFresherWork) ? (
                <Section title={workTitle} config={config} style={{ marginTop: 34 }} sectionKey={isExperienced ? "experience" : "projects"}>
                  {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                  {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
                </Section>
              ) : null}
              {model.education.length ? <Section title="Education" config={config} style={{ marginTop: 34 }} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} style={{ marginTop: 34 }} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </main>
          </div>
        </Shell>
      );
    case "sky":
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <Paper config={config} style={{ border: "none", padding: 0 }}>
            <div style={{ background: "#e0f2fe", minHeight: 176, padding: "38px 54px", display: "grid", alignItems: "center" }}>
              <header data-section="header" style={{ display: "grid", gridTemplateColumns: "86px minmax(0, 1fr)", gap: 26, alignItems: "center" }}>
                <Avatar model={model} config={config} size={86} dark style={{ background: accent, borderColor: accent, color: "#ffffff" }} />
                <div>
                  <h1 data-field="name" style={{ margin: 0, color: config.text, fontSize: 38, lineHeight: 1.04, fontWeight: 900 }}>{model.name}</h1>
                  <div data-field="roleTitle" style={{ color: accent, fontSize: 15, marginTop: 8 }}>{model.role}</div>
                  <ContactStrip model={model} config={config} style={{ marginTop: 14 }} />
                </div>
              </header>
            </div>
            <main style={{ padding: "62px 72px" }}>
              {hasAbout ? <Section title="About Me" config={config} style={{ marginBottom: 52 }} sectionKey="summary"><TextBlock text={model.about} color={config.text} style={{ fontSize: 14, lineHeight: 1.55 }} /></Section> : null}
              {model.skills.length ? <Section title="Skills" config={config} style={{ marginBottom: 58 }} sectionKey="skills"><Skills items={model.skills} config={config} mode="chips" /></Section> : null}
              {(hasExperience || hasFresherWork) ? (
                <Section title={workTitle} config={config} style={{ borderTop: `1px solid ${config.border}`, paddingTop: 34, marginBottom: 58 }} sectionKey={isExperienced ? "experience" : "projects"}>
                  {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="ledger" /> : null}
                  {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
                </Section>
              ) : null}
              <div className="np-grid-two">
                {model.education.length ? <Section title="Education" config={config} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
                {model.achievements.length ? <Section title="Achievements" config={config} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
              </div>
            </main>
          </Paper>
        </Shell>
      );
    case "luxury":
      return (
        <Shell config={config} style={{ background: config.paper, padding: 0 }}>
          <Paper config={config} style={{ borderTop: `12px solid ${accent}`, borderRight: "none", borderBottom: "none", borderLeft: "none", padding: "40px 36px" }}>
            <Header model={model} config={config} layout="split" style={{ marginBottom: 28 }} />
            {hasAbout ? <Section title="About Me" config={config} style={{ background: config.dark, padding: "16px 20px", marginBottom: 32 }} dark sectionKey="summary"><TextBlock text={model.about} color={config.paper} /></Section> : null}
            {model.skills.length ? <Section title="Skills" config={config} style={{ marginBottom: 34 }} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} style={{ marginBottom: 34 }} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="card" /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            <div className="np-grid-two">
              {model.education.length ? <Section title="Education" config={config} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
              {model.achievements.length ? <Section title="Achievements" config={config} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
            </div>
          </Paper>
        </Shell>
      );
    case "ats":
    default:
      return (
        <Shell config={config} style={{ padding: 0 }}>
          <Paper config={config} style={{ border: "none", padding: "40px 48px" }}>
            <Header model={model} config={config} layout="split" style={{ borderBottom: `1px solid ${config.border}`, paddingBottom: 18, marginBottom: 26 }} />
            {hasAbout ? <Section title="About Me" config={config} sectionKey="summary"><TextBlock text={model.about} color={config.text} /></Section> : null}
            {careerText ? <Section title="Career Objective" config={config} sectionKey="objective"><TextBlock text={careerText} color={config.text} dataField="objective" /></Section> : null}
            {model.skills.length ? <Section title="Skills" config={config} sectionKey="skills"><Skills items={model.skills} config={config} mode="inline" /></Section> : null}
            {(hasExperience || hasFresherWork) ? (
              <Section title={workTitle} config={config} sectionKey={isExperienced ? "experience" : "projects"}>
                {hasExperience ? <ExperienceList items={model.experiences} config={config} mode="ledger" /> : null}
                {hasFresherWork ? <><ProjectList items={model.projects} config={config} /><ReactList items={model.internships} dataField="internship" color={config.text} /></> : null}
              </Section>
            ) : null}
            {model.education.length ? <Section title="Education" config={config} sectionKey="education"><EducationList items={model.education} config={config} compact /></Section> : null}
            {certificationItems.length ? <Section title={isExperienced ? "Professional Certifications" : "Certifications & Training"} config={config} sectionKey="certifications"><ReactList items={certificationItems} dataField="certification" color={config.text} /></Section> : null}
            {leadershipItems.length ? <Section title="Leadership & Domain" config={config} sectionKey="domain"><ReactList items={leadershipItems} dataField="leadership" color={config.text} /></Section> : null}
            {model.achievements.length ? <Section title="Achievements" config={config} sectionKey="achievements"><ReactList items={model.achievements} color={config.text} /></Section> : null}
          </Paper>
        </Shell>
      );
  }
}

function bentoCard(config) {
  return {
    background: "rgba(255,255,255,0.9)",
    border: `1px solid ${config.border}`,
    borderRadius: 28,
    padding: 24,
  };
}

function productCard(config) {
  return {
    background: "#ffffff",
    border: `1px solid ${config.border}`,
    borderRadius: 24,
    padding: 22,
  };
}

function slateCard(config) {
  return {
    background: "#f8fafc",
    border: `1px solid ${config.border}`,
    padding: 22,
  };
}

function NewPrototypeTemplate({ templateId }) {
  const { token } = useToken();
  const level = useExperienceLevel();
  const model = usePrototypeModel();
  const baseConfig = PROTOTYPE_CONFIGS[templateId] || PROTOTYPE_CONFIGS.template34;
  const config = useMemo(
    () => ({
      ...baseConfig,
      templateId,
      dynamicAccent: pickAccent(token, baseConfig.accent),
    }),
    [baseConfig, templateId, token],
  );

  return renderLayout(config, model, level);
}

export const NEW_PROTOTYPE_TEMPLATE_OPTIONS = GENERATED_TEMPLATE_IDS.map((id) => ({
  id,
  name: PROTOTYPE_CONFIGS[id].name,
  preview: null,
  description: `Implemented from ${PROTOTYPE_CONFIGS[id].source}; adapts automatically for fresher and experienced flows.`,
  sourcePrototype: PROTOTYPE_CONFIGS[id].source,
  fidelity: PROTOTYPE_FIDELITY_SPECS[id],
}));

function createTemplate(templateId) {
  return function PrototypeTemplateComponent() {
    return <NewPrototypeTemplate templateId={templateId} />;
  };
}

export const OrbitExecutiveTemplate = createTemplate("template20");
export const SaasBentoTemplate = createTemplate("template21");
export const MonoLedgerTemplate = createTemplate("template22");
export const CoralRailTemplate = createTemplate("template23");
export const NeonCommandTemplate = createTemplate("template24");
export const EmeraldTimelineTemplate = createTemplate("template25");
export const EditorialSereneTemplate = createTemplate("template26");
export const ProductCardPrototypeTemplate = createTemplate("template27");
export const SlateGridTemplate = createTemplate("template28");
export const FounderBoardTemplate = createTemplate("template29");
export const IvorySerifTemplate = createTemplate("template30");
export const GraphiteSplitTemplate = createTemplate("template31");
export const SkyCleanTemplate = createTemplate("template32");
export const LuxuryRailTemplate = createTemplate("template33");
export const CompactAtsTemplate = createTemplate("template34");

export const NEW_PROTOTYPE_TEMPLATE_COMPONENTS = {
  template20: OrbitExecutiveTemplate,
  template21: SaasBentoTemplate,
  template22: MonoLedgerTemplate,
  template23: CoralRailTemplate,
  template24: NeonCommandTemplate,
  template25: EmeraldTimelineTemplate,
  template26: EditorialSereneTemplate,
  template27: ProductCardPrototypeTemplate,
  template28: SlateGridTemplate,
  template29: FounderBoardTemplate,
  template30: IvorySerifTemplate,
  template31: GraphiteSplitTemplate,
  template32: SkyCleanTemplate,
  template33: LuxuryRailTemplate,
  template34: CompactAtsTemplate,
};
